import { useQuery } from '@tanstack/react-query';

import type { UserFeatureToggles, UserResponse } from 'clients/UserClient/types';
import type { SupportedLanguage } from 'shared/legacy/contexts/LocalizationContext/types';
import type { CustomUseQueryOptions } from './types';

import { useActiveToken } from '@/shared/lib/auth';
import { userClient } from 'clients/UserClient/UserClient';
import { QueryKey } from 'types/query';

export type Response = UserResponse & {
  mailSignOff: {
    fullName: string;
    phoneNumber: string | undefined;
  };
  preferredLanguage?: SupportedLanguage;
};

export const useCurrentUserQuery = (options?: CustomUseQueryOptions<Response, [QueryKey]>) => {
  const activeToken = useActiveToken();

  return useQuery(
    [QueryKey.useCurrentUserQuery],
    async () => {
      const { data: user } = await userClient.getCurrent();

      const mailSignOff = {
        fullName: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
        phoneNumber: user?.phone_number ?? undefined,
      };

      const userLanguageFeature = user.features?.find((f) => f.name === ('SELECTED_LANGUAGE' as UserFeatureToggles));
      const preferredLanguage = userLanguageFeature?.value as SupportedLanguage | undefined;

      const currentUser: Response = {
        ...user,
        mailSignOff,
        preferredLanguage,
      };

      return currentUser;
    },
    { ...options, enabled: activeToken !== null },
  );
};
